import React, { useState, useEffect, useCallback } from 'react';
import {
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

interface LogEntry {
  raw: string;
  timestamp: string | null;
  level: string;
  message: string;
}

interface LogsData {
  logs: LogEntry[];
  total_lines: number;
  file: string;
  file_path: string;
  file_size: number;
}

export default function LogsViewer() {
  const [logsData, setLogsData] = useState<LogsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lines, setLines] = useState(100);
  const [level, setLevel] = useState<'all' | 'ERROR' | 'WARNING' | 'INFO' | 'DEBUG'>('all');
  const [search, setSearch] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const params: any = { lines };
      if (level !== 'all') {
        params.level = level;
      }
      if (search.trim()) {
        params.search = search.trim();
      }

      const data = await apiService.getLogs(params);
      setLogsData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar logs');
      console.error('Error loading logs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [lines, level, search]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleDownload = async () => {
    try {
      console.log('[LOGS VIEWER] Iniciando download de logs...');
      setIsDownloading(true);
      setError('');
      
      console.log('[LOGS VIEWER] Chamando apiService.downloadLogFile()...');
      const blob = await apiService.downloadLogFile();
      
      console.log('[LOGS VIEWER] Blob recebido:', {
        size: blob.size,
        type: blob.type,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `laravel-${new Date().toISOString().split('T')[0]}.log`;
      document.body.appendChild(link);
      console.log('[LOGS VIEWER] Iniciando download do arquivo...');
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log('[LOGS VIEWER] Download concluído com sucesso!');
    } catch (err: any) {
      console.error('[LOGS VIEWER] ERRO:', {
        message: err.message,
        code: err.code,
        status: err.response?.status,
        statusText: err.response?.statusText,
        isCORS: err.code === 'ERR_NETWORK' || err.message?.includes('CORS') || err.message?.includes('blocked'),
        headers: err.response?.headers,
        fullError: err,
      });
      setError(err.response?.data?.message || err.message || 'Erro ao baixar arquivo de log');
    } finally {
      setIsDownloading(false);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
      case 'WARNING':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      case 'INFO':
        return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
      case 'DEBUG':
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'ERROR':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'WARNING':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Visualizador de Logs
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Visualize e baixe os logs do sistema
          </p>
        </div>
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <DocumentArrowDownIcon className="h-5 w-5" />
          <span>{isDownloading ? 'Baixando...' : 'Baixar Log'}</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Lines */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Linhas
            </label>
            <input
              type="number"
              min="10"
              max="1000"
              value={lines}
              onChange={(e) => setLines(parseInt(e.target.value) || 100)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Level Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nível
            </label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">Todos</option>
              <option value="ERROR">Erros</option>
              <option value="WARNING">Avisos</option>
              <option value="INFO">Informações</option>
              <option value="DEBUG">Debug</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Buscar
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar termo..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end">
          <button
            onClick={loadLogs}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
          >
            <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Logs Info */}
      {logsData && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex justify-between items-center text-sm">
            <div>
              <span className="text-blue-700 dark:text-blue-300 font-medium">
                {logsData.total_lines} linhas
              </span>
              <span className="text-blue-600 dark:text-blue-400 ml-4">
                Arquivo: {logsData.file}
              </span>
              <span className="text-blue-600 dark:text-blue-400 ml-4">
                Tamanho: {(logsData.file_size / 1024).toFixed(2)} KB
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Logs Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : logsData && logsData.logs.length > 0 ? (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                    Nível
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Mensagem
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {logsData.logs.map((log, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${getLevelColor(
                          log.level
                        )}`}
                      >
                        {getLevelIcon(log.level)}
                        <span>{log.level}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                      {log.timestamp || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">
                      <pre className="whitespace-pre-wrap break-words">{log.message || log.raw}</pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">Nenhum log encontrado</p>
          </div>
        )}
      </div>
    </div>
  );
}

